import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import { NonCollectionRow, useNonCollectionRows } from "../model/use-non-collection-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import {
  amountCell,
  byNumber,
  compareIds,
  entityNumber,
  numberColumn,
  reportDate,
  sumOf,
  textCell,
} from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A report row, or the totals line that closes the exports. */
type NonCollectionExportRow = NonCollectionRow & { isTotal?: boolean };

/** «Детальный отчёт по невывозу» (AIS 15.1.7): every missed or cancelled pickup in the period. */
export const NonCollectionReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { rows, isLoading, error } = useNonCollectionRows(props.startDate, props.endDate);
    const { locationCell, customerCell, driverCell, plateCell, entityCards } =
      useReportHyperlinks();

    const columns: Column<NonCollectionExportRow>[] = useMemo(() => {
      return [
        numberColumn(
          t(Namespace.Analytics, "columns.number"),
          t(Namespace.Analytics, "collection.totals.label"),
        ),
        {
          id: "id",
          header: t(Namespace.Analytics, "columns.id"),
          accessor: (row) => (row.isTotal ? "" : entityNumber("job", row.jobId)),
          width: 100,
          sortable: true,
          sortFn: (a, b) => compareIds(a.jobId, b.jobId),
        },
        {
          id: "exportDate",
          header: t(Namespace.Analytics, "columns.exportDate"),
          accessor: (row) => (row.isTotal ? "" : reportDate(row.twStartAt)),
          width: 120,
          sortable: true,
          sortFn: byNumber((row) => row.twStartAt?.getTime()),
        },
        {
          id: "site",
          header: t(Namespace.Analytics, "columns.site"),
          key: "pickupLocationName",
          width: 200,
          sortable: true,
          cell: (info) =>
            locationCell(info.row.original.pickupLocationId, info.row.original.pickupLocationName),
        },
        {
          id: "photo",
          header: t(Namespace.Analytics, "nonCollection.photo"),
          accessor: (row) =>
            row.isTotal
              ? ""
              : t(Namespace.Analytics, row.hasPhotoReport ? "values.yes" : "values.no"),
          width: 90,
          sortable: true,
        },
        {
          id: "client",
          header: t(Namespace.Analytics, "table.customer"),
          key: "serviceReceiverName",
          width: 200,
          sortable: true,
          cell: (info) =>
            customerCell(
              info.row.original.serviceReceiverId,
              info.row.original.serviceReceiverName,
            ),
        },
        {
          id: "bin",
          header: t(Namespace.Analytics, "columns.bin"),
          accessor: (row) => textCell(row.serviceReceiverBin, row.isTotal),
          width: 130,
          sortable: true,
        },
        {
          id: "containerType",
          header: t(Namespace.Analytics, "columns.containerType"),
          accessor: (row) => textCell(row.containerTypeName, row.isTotal),
          width: 160,
          sortable: true,
        },
        {
          id: "driver",
          header: t(Namespace.Analytics, "collection.driver"),
          key: "driverName",
          width: 170,
          sortable: true,
          cell: (info) => driverCell(info.row.original.driverId, info.row.original.driverName),
        },
        {
          id: "truck",
          header: t(Namespace.Analytics, "collection.truck"),
          key: "truckPlateNumber",
          width: 120,
          sortable: true,
          cell: (info) => plateCell(info.row.original.truckId, info.row.original.truckPlateNumber),
        },
        {
          id: "count",
          header: t(Namespace.Analytics, "collection.count"),
          accessor: (row) => amountCell(row.numberOfContainers, 0, row.isTotal),
          width: 120,
          sortable: true,
          sortFn: byNumber((row) => row.numberOfContainers),
        },
        {
          id: "volume",
          header: t(Namespace.Analytics, "columns.nominalVolume"),
          accessor: (row) => amountCell(row.containerVolume, 2, row.isTotal),
          width: 130,
          sortable: true,
          sortFn: byNumber((row) => row.containerVolume),
        },
        {
          id: "comment",
          header: t(Namespace.Analytics, "nonCollection.comment"),
          accessor: (row) => textCell(row.driverComment, row.isTotal),
          width: 240,
          sortable: true,
        },
        {
          id: "action",
          header: t(Namespace.Analytics, "nonCollection.action"),
          // «Невывоз» / «Отменено», the job status labels.
          accessor: (row) =>
            row.isTotal ? "" : t(Namespace.Enum, `JOB_STATUS_${row.status}` as TranslationKey),
          width: 130,
          sortable: true,
        },
      ];
    }, [t, locationCell, customerCell, driverCell, plateCell]);

    const exportRows = useMemo<NonCollectionExportRow[]>(
      () =>
        rows.length
          ? [
              ...rows,
              {
                isTotal: true,
                jobId: 0n,
                twStartAt: null,
                pickupLocationName: "",
                hasPhotoReport: false,
                serviceReceiverName: "",
                serviceReceiverBin: "",
                containerTypeName: "",
                driverName: "",
                truckPlateNumber: "",
                numberOfContainers: sumOf(rows.map((r) => r.numberOfContainers)),
                containerVolume: sumOf(rows.map((r) => r.containerVolume)),
                driverComment: "",
                status: "",
              },
            ]
          : rows,
      [rows],
    );

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="non_collection_report"
        reportName={t(Namespace.Analytics, "reports.nonCollection")}
        columns={columns}
        rows={rows}
        exportRows={exportRows}
        rowKey={(row) => String(row.jobId)}
        isLoading={isLoading}
        error={error}
      >
        {entityCards}
      </ReportTable>
    );
  },
);
